import { useState } from "react";
import { X } from "lucide-react";
import DemoStudentProfile, { DEMO_PROFILES } from "@/components/demo/DemoStudentProfile";

/*
 * Live discussion example for the guided demo. Sample students and replies
 * only. Points, notes and messages stay on screen and are never saved or sent.
 */

type Sample = { id: string; name: string; initials: string; tone: string; when: string; text: string };

const SAMPLES: Sample[] = [
  {
    id: "maya",
    name: "Maya R.",
    initials: "MR",
    tone: "teal",
    when: "2 days ago",
    text: "My phone bill is $30 a month plus $5 for every gigabyte, so 30 + 5g. I picked g because it stands for gigabytes.",
  },
  {
    id: "jordan",
    name: "Jordan T.",
    initials: "JT",
    tone: "amber",
    when: "2 days ago",
    text: "In basketball my points are 2s + 3t. s is two-pointers and t is threes, because the letters match the words.",
  },
  { id: "ava", name: "Ava L.", initials: "AL", tone: "violet", when: "Yesterday", text: "I just use x for everything." },
  {
    id: "diego",
    name: "Diego M.",
    initials: "DM",
    tone: "green",
    when: "Yesterday",
    text: "A ride costs 2.50 + 1.75m, where m is the miles. I used m so I would not forget what it means.",
  },
];

type Grade = { score: number; note: string };

export type DiscussionSpot = "grade" | "dm" | "profile" | null;

export default function DemoDiscussion({
  prompt,
  points,
  spot,
  course = "Algebra I",
}: {
  prompt: string;
  points: number;
  spot: DiscussionSpot;
  /** Course name used in the sample profile and parent messages. */
  course?: string;
}) {
  const [graded, setGraded] = useState<Record<string, Grade>>({
    maya: { score: 9, note: "Great real-life example!" },
  });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [dmFor, setDmFor] = useState<Sample | null>(null);
  const [dmText, setDmText] = useState("");
  const [dmLog, setDmLog] = useState<string[]>([]);
  const [profileFor, setProfileFor] = useState<Sample | null>(null);
  const max = points || 10;
  const firstOpen = SAMPLES.find((s) => !graded[s.id]);

  /** A typed score is valid from 0 to the item's points, in steps of a half point. */
  const parse = (raw: string | undefined): number | null => {
    if (raw == null || raw.trim() === "") return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > max || Math.round(n * 2) !== n * 2) return null;
    return n;
  };

  const give = (id: string) => {
    const score = parse(drafts[id]);
    if (score == null) return;
    setGraded((all) => ({ ...all, [id]: { score, note: (notes[id] || "").trim() } }));
  };

  const regrade = (id: string) => {
    const current = graded[id];
    if (current) {
      setDrafts((all) => ({ ...all, [id]: String(current.score) }));
      setNotes((all) => ({ ...all, [id]: current.note }));
    }
    setGraded((all) => {
      const next = { ...all };
      delete next[id];
      return next;
    });
  };

  const openDm = (sample: Sample) => {
    setDmFor(sample);
    setDmText("");
    setDmLog([]);
  };

  const sendDm = () => {
    const text = dmText.trim();
    if (!text) return;
    setDmLog((log) => [...log, text]);
    setDmText("");
  };

  return (
    <div className="al-demo-disc">
      <div className="al-demo-disc-banner">Live example with sample students. Nothing here is saved or sent.</div>
      {prompt ? <p className="al-demo-disc-prompt">{prompt}</p> : null}
      <p className="al-demo-disc-count">
        Showing {SAMPLES.length} sample replies · {Object.keys(graded).length} graded. Open Results for the whole class.
      </p>

      <ol className="al-demo-replies">
        {SAMPLES.map((sample) => {
          const grade = graded[sample.id];
          const draft = drafts[sample.id] ?? "";
          const score = parse(draft);
          const invalid = draft.trim() !== "" && score == null;
          const ringDm = spot === "dm" && sample.id === "maya";
          const ringGrade = spot === "grade" && firstOpen?.id === sample.id;
          return (
            <li key={sample.id} className="al-demo-reply">
              <button
                type="button"
                className={`al-demo-ava al-demo-ava--${sample.tone}${ringDm ? " al-demo-spot" : ""}`}
                onClick={() => openDm(sample)}
                aria-label={`Message ${sample.name}`}
                title={`Message ${sample.name}`}
                data-tour={sample.id === "maya" ? "demo-dm" : undefined}
              >
                {sample.initials}
              </button>
              <div className="al-demo-reply-body">
                <p className="al-demo-reply-who">
                  <button
                    type="button"
                    className={`al-demo-reply-name${spot === "profile" && sample.id === "ava" ? " al-demo-spot" : ""}`}
                    onClick={() => setProfileFor(sample)}
                    title={`Open ${sample.name}'s profile`}
                    data-tour={sample.id === "ava" ? "demo-profile" : undefined}
                  >
                    {sample.name}
                  </button>{" "}
                  <span className="al-demo-sample">Sample student</span>{" "}
                  <span className="al-demo-reply-when">{sample.when}</span>
                </p>
                <p className="al-demo-reply-text">{sample.text}</p>
                {grade ? (
                  <div className="al-demo-graded">
                    <span className="al-demo-grade">
                      {grade.score} / {max}
                    </span>
                    {grade.note ? <span className="al-demo-graded-note">“{grade.note}”</span> : null}
                    <button type="button" className="al-demo-link" onClick={() => regrade(sample.id)}>
                      Change
                    </button>
                  </div>
                ) : (
                  <div className={`al-demo-grader${ringGrade ? " al-demo-spot" : ""}`} data-tour={ringGrade ? "demo-grade" : undefined}>
                    <div className="al-demo-scores">
                      <label htmlFor={`demo-grade-${sample.id}`}>Grade</label>
                      <input
                        id={`demo-grade-${sample.id}`}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        max={max}
                        step="0.5"
                        className="al-demo-score-input"
                        value={draft}
                        aria-invalid={invalid}
                        aria-describedby={`demo-grade-help-${sample.id}`}
                        onChange={(event) => setDrafts((all) => ({ ...all, [sample.id]: event.target.value }))}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") give(sample.id);
                        }}
                      />
                      <span>/ {max}</span>
                      <span id={`demo-grade-help-${sample.id}`} className={`al-demo-score-help${invalid ? " is-bad" : ""}`}>
                        {invalid
                          ? `Enter 0 to ${max}`
                          : score != null
                            ? `${Math.round((score / max) * 100)}%`
                            : ""}
                      </span>
                    </div>
                    <div className="al-demo-note-row">
                      <label className="al-demo-sr" htmlFor={`demo-note-${sample.id}`}>
                        Comment for {sample.name}
                      </label>
                      <input
                        id={`demo-note-${sample.id}`}
                        type="text"
                        value={notes[sample.id] || ""}
                        onChange={(event) => setNotes((all) => ({ ...all, [sample.id]: event.target.value }))}
                        placeholder="Comment for the student (optional)"
                      />
                      <button type="button" className="al-demo-btn" onClick={() => give(sample.id)} disabled={score == null}>
                        Save mark
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {profileFor && DEMO_PROFILES[profileFor.id] ? (
        <DemoStudentProfile
          student={DEMO_PROFILES[profileFor.id]}
          course={course}
          onClose={() => setProfileFor(null)}
          onMessageStudent={() => {
            const who = profileFor;
            setProfileFor(null);
            openDm(who);
          }}
        />
      ) : null}

      {dmFor ? (
        <div className="al-demo-dm-backdrop" onClick={() => setDmFor(null)} role="presentation">
          <div
            className="al-demo-dm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="al-demo-dm-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="al-demo-dm-head">
              <span className={`al-demo-ava al-demo-ava--${dmFor.tone}`} aria-hidden="true">
                {dmFor.initials}
              </span>
              <div>
                <p id="al-demo-dm-title" className="al-demo-dm-title">
                  Message {dmFor.name}
                </p>
                <p className="al-demo-dm-sub">Demo only. Nothing is delivered.</p>
              </div>
              <button type="button" className="al-demo-dm-close" onClick={() => setDmFor(null)} aria-label="Close">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="al-demo-dm-log">
              {dmLog.map((text, index) => (
                <p key={index} className="al-demo-dm-msg">
                  {text}
                </p>
              ))}
              {dmLog.length ? <p className="al-demo-dm-status">Shown here only. In the real app this goes to the student's Messages.</p> : null}
            </div>
            <form
              className="al-demo-dm-form"
              onSubmit={(event) => {
                event.preventDefault();
                sendDm();
              }}
            >
              <label className="al-demo-sr" htmlFor="al-demo-dm-text">
                Message
              </label>
              <input
                id="al-demo-dm-text"
                type="text"
                value={dmText}
                onChange={(event) => setDmText(event.target.value)}
                placeholder="Great reply! Can you add one more example?"
                autoFocus
              />
              <button type="submit" className="al-demo-btn">
                Send
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
